/*
 *  Collection Template
 *    Contains all the scripts used for the collection template.
 *
 *  Version:
 *    1.0.0 - 2021/11/18
 */

import $ from 'jquery';
import {
  AjaxCollectionTemplate,
  OPERATION_AND,
  SmartTagButtonFacet,
  SelectSorter,
} from '@process-creative/slate-ajax-collection';

import { t } from '../../tools/utilities/string-tools';
import {
  CLASS_HIDDEN,
  CLASS_VISIBLE,
  CLASS_OPEN,
} from '../../tools/constants/classes';
import { initialiseThumbnails } from '../../objects/product/ProductThumbnail';
import { productThumbnailPrint } from '../../objects/product/product-thumbnail-print';

import { CustomTagFilter } from './filters/CustomTagFilter';
import { CustomPageNumbers } from './pagination/CustomPageNumbers';

// Attributes
const ATTR_CONTENT_BLOCK_POSITION = 'data-content-block-position';

// Selectors
const SELECTOR_COLLECTION_TEMPLATE = '[data-collection-template]';

const SELECTOR_FACETED_NAV = '[data-faceted-nav]';
const SELECTOR_FACETED_NAV_GROUP = '[data-faceted-nav-group]';
const SELECTOR_FACETED_NAV_CATEGORY = '[data-faceted-nav-category]';

const SELECTOR_SORT_SELECT = '[data-sort-custom-inner]';

const SELECTOR_NO_RESULTS = '[data-collection-no-results]';
const SELECTOR_FILTERS = '[data-filters]';
const SELECTOR_PRODUCTS_COUNT = '[data-collection-count-selector]';

const SELECTOR_PAGINATION = '[data-collection-template-pagination]';

class CollectionTemplate extends AjaxCollectionTemplate {
  constructor(container) {
    super(container);
    this.container = container;
    this.usesNewCollectionItems = container.is('[data-collection-uses-new-collection-items]');
    this.pagination.setPaginator(
      new CustomPageNumbers(this, container.find(SELECTOR_PAGINATION))
    );
    this.setupCollection();
    this.init();
  }

  setupCollection() {
    this.setupFiltersAndFacets();
    this.setElements();
    this.setupEvents();
    // Custom content positioning
    this.pagination.paginateContentAware = true;
  }

  setupFiltersAndFacets() {
    this.draw.setDisplayMode('FirstAvailableVariant');

    // Setup Filters
    this.categoryFilter = new CustomTagFilter(this, 'category', OPERATION_AND);
    this.filters.addFilter(this.categoryFilter);

    // Setup Facets

    this.facet = new SmartTagButtonFacet({
      template: this,
      filter: this.categoryFilter,
      title: t('collections.collection.category'),
      container: SELECTOR_FACETED_NAV_CATEGORY,
      prefixes: ['category'],
      token: '_',
      hasAll: true,
      allName: 'Shop All',
    });
    this.filters.addFacet(this.facet);
  }

  setElements() {
    // Elements
    this.count = this.container.find(SELECTOR_PRODUCTS_COUNT);
    this.noResults = this.container.find(SELECTOR_NO_RESULTS);
    this.facets = this.container.find(SELECTOR_FACETED_NAV);
    this.facetToggle = [this.facets, $('body')];
    this.facetGroups = this.container.find(SELECTOR_FACETED_NAV_GROUP);

    // Sort
    this.sort.addSorter(
      new SelectSorter(this, this.container.find(SELECTOR_SORT_SELECT))
    );

    this.sort.sortMethods.BestSelling.name = 'Best Selling';
    this.sort.sortMethods.TitleAscending.name = 'Alphabetically, A - Z';
    this.sort.sortMethods.TitleDescending.name = 'Alphabetically, Z - A';
    this.sort.sortMethods.CreatedAscending.name = 'Oldest to Newest';
    this.sort.sortMethods.CreatedDescending.name = 'Newest to Oldest';

    // Filters
    this.filtersContainer = this.container.find(SELECTOR_FILTERS);
  }

  setupEvents() {
    document
      .querySelector(SELECTOR_FACETED_NAV_CATEGORY)
      .addEventListener('click', (evt) => this.onFacetClick(evt));

    // Custom content positioning
    this.pagination.paginateContentAware = true;
    this.printedContentBlocks = [];
  }

  contentBlockIsOnPage(contentBlock, perPage, currentPage, productsShown) {
    // OG Position of the block from the data attr
    const blockOriginalPosition =
      parseInt(contentBlock.attr('data-content-block-position'), 10) || 999;

    // Index of the block in the printed blocks array
    const blockIndex = this.printedContentBlocks.indexOf(blockOriginalPosition);

    // How many content blocks have been printed and what is their size?
    const offset = this.getContentBlockSize();
    const countOfPrintedBlocks = this.printedContentBlocks.length * offset;
    let blockNewPosition = blockOriginalPosition + countOfPrintedBlocks;

    if (blockIndex !== -1) blockNewPosition -= offset;

    // Hide blocks with index past number of products
    if (blockOriginalPosition > productsShown) return false;

    const shouldBlockBeOnPage =
      blockNewPosition < currentPage * perPage &&
      blockNewPosition > (currentPage - 1) * perPage;

    if (shouldBlockBeOnPage && blockIndex === -1) {
      this.printedContentBlocks.push(blockOriginalPosition);
    } else {
      this.printedContentBlocks.splice(blockIndex, 1);
    }

    return shouldBlockBeOnPage;
  }

  getContentBlockPosition(cb, thumbnails) {
    // No thumbnails? hide
    if (thumbnails.length <= 0) return -1;

    const shouldBlockBeOnPage = this.contentBlockIsOnPage(
      cb,
      this.pagination.template.perPage,
      this.pagination.getCurrentPage(),
      thumbnails.length
    );

    if (shouldBlockBeOnPage) {
      cb.addClass(CLASS_VISIBLE);
    } else {
      cb.removeClass(CLASS_VISIBLE);
      return undefined;
    }

    const originalPosition = parseInt(cb.attr(ATTR_CONTENT_BLOCK_POSITION), 10);
    return originalPosition;
  }

  getContentBlocksForPage(params) {
    const productsShown = params.paginator.template.draw.drawnProducts.length;

    try {
      const contentBlocks = params.contentBlocks.filter((contentBlock) => {
        return this.contentBlockIsOnPage(
          $(contentBlock),
          params.perPage,
          params.page,
          productsShown
        );
      });

      return contentBlocks.length ? contentBlocks : -1;
    } catch (err) {
      console.error(err);
      return -1;
    }
  }

  getContentBlockSize() {
    return 1;
  }

  generatePrint(vrnt) {
    const prdct = vrnt.product;
    // Duplicate
    const prdct2 = {
      ...prdct,
    };
    prdct2.variants = prdct2.variants.map((variant) => {
      // Duplicate
      const vrnt2 = {
        ...variant,
      };
      // Remove circular reference
      delete vrnt2.product;
      return vrnt2;
    });
    return productThumbnailPrint(prdct2, 'c-collection', this.usesNewCollectionItems);
  }

  onVariantsDrawn(variants) {
    if (variants && variants.length) {
      this.count.removeClass(CLASS_HIDDEN);
      this.noResults.removeClass(CLASS_VISIBLE);
      initialiseThumbnails();
    } else {
      this.count.addClass(CLASS_HIDDEN);
      this.noResults.addClass(CLASS_VISIBLE);
    }

    const unpaginatedProductCount = this.draw.unpaginatedProductCount;
    if (
      unpaginatedProductCount !== '' &&
      unpaginatedProductCount !== undefined
    ) {
      // display number of products visible in the page count
      const message = t('collections.collection.product_count', {
        count: variants.length,
        total: unpaginatedProductCount,
      });

      this.count.html(message);
    }
  }

  onFacetClick() {
    const fakeSelect = this.container.find(SELECTOR_FACETED_NAV_CATEGORY);
    fakeSelect[0].classList.toggle(CLASS_OPEN);
  }
}

$(() => {
  const container = $(SELECTOR_COLLECTION_TEMPLATE);
  if (!container || !container.length) return;
  if (container.attr('data-initialized')) return;

  // Create the template
  window.collection = new CollectionTemplate(container);
});
